/**
 * AI Message API — SSE streaming
 *
 * Uses raw fetch with Accept: text/event-stream rather than the standard
 * apiFetch wrapper, because SSE responses are not JSON-wrapped
 * StandardApiResponse objects.
 *
 * Routes mount at /api/ai-core/* (platform convention: /api/<pluginId>/<route>).
 */

import { getApiHeaders } from '@lognai/plugin-sdk';
import type { AiMessage } from '@lognai/plugin-ai-core';
import { apiFetch } from './client';

const BASE_URL = '/api/ai-core';

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

/**
 * Fetch persisted messages on a thread (oldest → newest).
 */
export const listAiMessages = async (
  threadId: string,
  opts?: { limit?: number },
): Promise<AiMessage[]> => {
  const qs = opts?.limit ? `?limit=${opts.limit}` : '';
  return apiFetch<AiMessage[]>(
    `${BASE_URL}/threads/${encodeURIComponent(threadId)}/messages${qs}`,
    { credentials: 'include' },
  );
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AiChatStreamEvent =
  | { type: 'token'; data: { text: string } }
  | { type: 'tool_use_started'; data: { id: string; name: string; uiLabel?: string } }
  | { type: 'tool_use_result'; data: { trace: unknown } }
  | { type: 'draft_created'; data: { draftId: string; bundleId: string; riskBand: 'low' | 'medium' | 'high'; uiLabel: string; uiSummary: string } }
  | { type: 'draft_auto_applied'; data: { draftId: string; bundleId: string; output: unknown; undoDeadlineAt: string } }
  | { type: 'bundle_ready'; data: { bundleId: string; itemCount: number; riskBandMax: 'low' | 'medium' | 'high'; summaryForChat: string } }
  | { type: 'bundle_available'; data: { bundleId: string; reason: 'duplicate' | 'fallback' | 'create_failed'; itemCount?: number; riskBandMax?: 'low' | 'medium' | 'high'; message?: string; orphanedEntityId?: string } }
  | { type: 'bundle_rejected_conflict'; data: { attemptedDraftKind?: string; conflictingDraftId: string } }
  | { type: 'writes_disabled'; data: { toolName: string } }
  | { type: 'message_persisted'; data: { userMessageId: string; assistantMessageId: string } }
  | { type: 'error'; data: { code: string; message: string } }
  | { type: 'done'; data: Record<string, never> };

export interface SendAiMessageInput {
  threadId: string;
  content: string;
  pageContext?: {
    pluginId: string;
    entityType: string;
    entityId: string;
    url: string;
  };
}

// ---------------------------------------------------------------------------
// Streaming function
// ---------------------------------------------------------------------------

/**
 * Send a message to an AI thread and stream back SSE events.
 *
 * Usage:
 *   for await (const event of streamAiMessage({ threadId, content })) { ... }
 */
export const streamAiMessage = async function* (
  input: SendAiMessageInput,
  signal?: AbortSignal
): AsyncIterable<AiChatStreamEvent> {
  const res = await fetch(
    `${BASE_URL}/threads/${encodeURIComponent(input.threadId)}/messages`,
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ content: input.content, pageContext: input.pageContext }),
      credentials: 'include',
      signal,
    }
  );

  if (!res.ok || !res.body) {
    throw new Error(`AI chat failed: ${res.status} ${res.statusText}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      let type = 'message';
      let data = '';
      for (const line of raw.split('\n')) {
        if (line.startsWith('event: ')) type = line.slice(7).trim();
        else if (line.startsWith('data: ')) data += line.slice(6);
      }
      if (!data) continue;
      try {
        const parsed = JSON.parse(data) as AiChatStreamEvent['data'];
        yield { type, data: parsed } as AiChatStreamEvent;
      } catch {
        // skip malformed chunk
      }
    }
  }
};
